import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Search, Undo2, PackageCheck } from "lucide-react";
import { useSales, useReturnableLines, useCreateSaleReturn, useSaleReturns, PAYMENT_METHOD_LABEL, PaymentMethod } from "@/api/pos";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

/**
 * Devoluciones: a diferencia de "Anular venta" (que revierte la venta
 * ENTERA), aquí se devuelven solo algunos ítems de una venta ya completada
 * — el caso normal en una tienda (el cliente devuelve 1 de 5 productos).
 * Repone el stock de la sucursal de la venta original. El cajero entrega
 * el dinero por fuera del sistema, pero si el reembolso es en EFECTIVO y
 * hay un turno de caja abierto en ese momento, se descuenta automáticamente
 * del efectivo esperado al cerrar ese turno (antes esto era solo
 * informativo y el cierre de caja no se enteraba).
 */
export default function ReturnsPage() {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("sale");
  const [saleNumberInput, setSaleNumberInput] = useState(preselected ?? "");
  const [activeSaleNumber, setActiveSaleNumber] = useState<number | null>(preselected ? Number(preselected) : null);

  useEffect(() => {
    if (preselected) {
      setSaleNumberInput(preselected);
      setActiveSaleNumber(Number(preselected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselected]);

  const { data: saleSearch, isLoading: searching } = useSales({ saleNumber: activeSaleNumber ?? undefined, pageSize: 1 });
  const foundSale = saleSearch?.data?.[0] ?? null;

  const { data: historyResponse } = useSaleReturns({ pageSize: 20 });
  const history = historyResponse?.data ?? [];

  const handleSearch = () => {
    const n = Number(saleNumberInput.trim());
    if (!n || n <= 0) {
      toast.error("Ingresa un número de venta válido.");
      return;
    }
    setActiveSaleNumber(n);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Devoluciones</h1>
        <p className="text-sm text-gray-500">Devuelve uno o varios productos de una venta ya completada — repone el stock automáticamente.</p>
      </div>

      <div className="card p-4">
        <label className="block text-sm font-medium text-gray-700">Buscar venta por número</label>
        <div className="mt-1 flex gap-2">
          <input
            className="input-field flex-1"
            placeholder="Ej: 128"
            value={saleNumberInput}
            onChange={(e) => setSaleNumberInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching}>
            <Search size={16} className="mr-1.5" /> Buscar
          </button>
        </div>
        {activeSaleNumber !== null && !searching && !foundSale && <p className="mt-2 text-sm text-red-600">No se encontró ninguna venta con el número #{activeSaleNumber}.</p>}
      </div>

      {foundSale && <ReturnPanel saleId={foundSale.id} saleNumber={foundSale.saleNumber} onDone={() => setActiveSaleNumber(activeSaleNumber)} />}

      <div className="card overflow-hidden">
        <p className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900">Devoluciones registradas</p>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-500">Productos</th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-500">Registrada por</th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-500">Medio</th>
              <th className="px-4 py-2.5 text-right font-medium text-gray-500">Reembolso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {history.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Todavía no se han registrado devoluciones.
                </td>
              </tr>
            )}
            {history.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-600">{new Date(r.createdAt).toLocaleString("es-CO")}</td>
                <td className="px-4 py-2.5 text-gray-600">{r.branch?.name ?? "—"}</td>
                <td className="px-4 py-2.5 text-gray-600">{r.lines.map((l) => `${l.product?.name ?? "Producto"} ×${l.quantity}`).join(", ")}</td>
                <td className="px-4 py-2.5 text-gray-600">{r.createdBy?.fullName ?? "—"}</td>
                <td className="px-4 py-2.5 text-gray-600">{PAYMENT_METHOD_LABEL[r.refundMethod]}</td>
                <td className="px-4 py-2.5 text-right font-medium text-gray-900">{currency.format(Number(r.totalRefund))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReturnPanel({ saleId, saleNumber, onDone }: { saleId: string; saleNumber: number; onDone: () => void }) {
  const { data, isLoading } = useReturnableLines(saleId);
  const createReturn = useCreateSaleReturn();
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [refundMethod, setRefundMethod] = useState<PaymentMethod>("EFECTIVO");

  const setQty = (saleLineId: string, value: string) => setQuantities((prev) => ({ ...prev, [saleLineId]: value }));

  const linesToReturn = (data?.lines ?? []).filter((l) => Number(quantities[l.saleLineId] ?? 0) > 0);
  const estimatedRefund = linesToReturn.reduce((sum, l) => {
    const qty = Math.min(Number(quantities[l.saleLineId] ?? 0), l.returnable);
    return sum + qty * l.unitPrice;
  }, 0);

  const handleSubmit = async () => {
    if (linesToReturn.length === 0) {
      toast.error("Indica la cantidad a devolver de al menos un producto.");
      return;
    }
    for (const l of linesToReturn) {
      const qty = Number(quantities[l.saleLineId]);
      if (qty > l.returnable) {
        toast.error(`No puedes devolver más de ${l.returnable} de "${l.product?.name}".`);
        return;
      }
    }
    try {
      await createReturn.mutateAsync({
        saleId,
        payload: {
          lines: linesToReturn.map((l) => ({ saleLineId: l.saleLineId, quantity: quantities[l.saleLineId] })),
          notes: notes || undefined,
          refundMethod,
        },
      });
      toast.success("Devolución registrada — stock repuesto.");
      setQuantities({});
      setNotes("");
      setRefundMethod("EFECTIVO");
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  if (isLoading) return <div className="card p-4 text-sm text-gray-400">Cargando venta...</div>;
  if (!data) return null;

  if (data.sale.status === "ANULADA") {
    return <div className="card p-4 text-sm text-red-600">La venta #{saleNumber} está anulada — no se puede devolver nada de ella.</div>;
  }

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">
          Venta #{saleNumber} · {new Date(data.sale.saleDate).toLocaleString("es-CO")} · {PAYMENT_METHOD_LABEL[data.sale.paymentMethod]}
        </p>
        <span className="text-sm text-gray-500">Total: {currency.format(Number(data.sale.total))}</span>
      </div>

      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-500">Producto</th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">Vendido</th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">Ya devuelto</th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">Disponible</th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">A devolver</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.lines.map((l) => (
            <tr key={l.saleLineId}>
              <td className="px-3 py-2 text-gray-900">{l.product?.name ?? "Producto"}</td>
              <td className="px-3 py-2 text-right text-gray-500">{l.quantitySold}</td>
              <td className="px-3 py-2 text-right text-gray-500">{l.alreadyReturned}</td>
              <td className="px-3 py-2 text-right text-gray-500">{l.returnable}</td>
              <td className="px-3 py-2 text-right">
                <input
                  type="number"
                  min={0}
                  max={l.returnable}
                  className="input-field w-20 text-right"
                  disabled={l.returnable <= 0}
                  value={quantities[l.saleLineId] ?? ""}
                  onChange={(e) => setQty(l.saleLineId, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo (opcional)</label>
          <input className="input-field mt-1" placeholder="Ej: producto defectuoso, cliente cambió de idea..." value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Medio de reembolso</label>
          <select className="input-field mt-1 w-auto" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value as PaymentMethod)}>
            {Object.entries(PAYMENT_METHOD_LABEL)
              .filter(([value]) => value !== "CREDITO")
              .map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
          </select>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Reembolso estimado</p>
          <p className="text-lg font-semibold text-gray-900">{currency.format(estimatedRefund)}</p>
        </div>
      </div>
      {refundMethod === "EFECTIVO" && (
        <p className="mt-1 text-xs text-gray-400">Se descontará del efectivo esperado al cerrar el turno de caja actual de esta sucursal (si hay uno abierto).</p>
      )}

      <button type="button" className="btn-primary mt-3 w-full" disabled={createReturn.isPending || linesToReturn.length === 0} onClick={handleSubmit}>
        {createReturn.isPending ? (
          "Registrando..."
        ) : (
          <>
            <Undo2 size={16} className="mr-1.5" /> Registrar devolución
          </>
        )}
      </button>
      {data.lines.every((l) => l.returnable <= 0) && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-400">
          <PackageCheck size={14} /> Ya se devolvió todo lo vendido en esta venta.
        </p>
      )}
    </div>
  );
}
